import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { PostCardAuthProvider } from "@/domains/PostCreator/contexts/PostCardAuthContext";
import { ClientFeedPost } from "./ClientFeedPost";
import { UserRole } from "@/services/urlService";
import { DocumentIcon } from "@/components/icons";
import { ReviewPager } from "@/components/ui/ReviewPager";
import { ScrollMoreHint } from "@/components/ui/ScrollMoreHint";
import type { Post } from "@/core/types/postTypes";

interface PostReviewViewProps {
  clientId: string;
  token?: string;
  pendingPosts: Post[];
  onReviewPost: (postId: string, isReviewed: boolean) => Promise<void>;
  onUpdate: (postId: string, updates: { caption?: string; status?: string }) => Promise<void>;
}

export function PostReviewView({
  clientId,
  token,
  pendingPosts,
  onReviewPost,
  onUpdate,
}: PostReviewViewProps) {
  const [searchParams] = useSearchParams();
  const targetPostId = searchParams.get("post");

  const scrollRef = useRef<HTMLDivElement>(null);

  const [currentIndex, setCurrentIndex] = useState(() => {
    if (!targetPostId || pendingPosts.length === 0) return 0;
    const idx = pendingPosts.findIndex((p) => p.id === targetPostId);
    return idx >= 0 ? idx : 0;
  });

  // Clamp index when list changes (approve/reject removes a post)
  useEffect(() => {
    if (pendingPosts.length === 0) {
      setCurrentIndex(0);
      return;
    }
    if (currentIndex >= pendingPosts.length) {
      setCurrentIndex(pendingPosts.length - 1);
    }
  }, [pendingPosts.length, currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < pendingPosts.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, pendingPosts.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNext, handlePrevious]);

  // Empty state
  if (pendingPosts.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600 font-medium">
          Nessun post da revisionare
        </p>
        <p className="text-gray-500 text-sm mt-1">
          Tutti i post sono stati revisionati
        </p>
      </div>
    );
  }

  const currentPost = pendingPosts[currentIndex];

  return (
    <PostCardAuthProvider
      userRole={UserRole.CLIENT}
      clientId={clientId}
      authToken={token}
    >
      <div className="flex h-full min-h-0 flex-col bg-gray-50">
        {/* Scroll region — owns vertical overflow so the pager bar
            below it sits naturally above any tap targets. */}
        <div ref={scrollRef} className="relative flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl py-4 px-4 lg:px-6">
            <ClientFeedPost
              key={currentPost.id}
              post={currentPost}
              gridId=""
              clientId={clientId}
              isFocused={true}
              onReviewPost={onReviewPost}
              onPostUpdated={() => {
                // Data refreshes through the parent outlet context
              }}
            />
          </div>
          <ScrollMoreHint
            scrollRef={scrollRef}
            storageKey="post-review"
            label="Scorri per approvare"
          />
        </div>

        <ReviewPager
          currentIndex={currentIndex}
          total={pendingPosts.length}
          onPrev={handlePrevious}
          onNext={handleNext}
          reviewedFlags={pendingPosts.map((p) => Boolean(p.clientReviewedAt))}
        />
      </div>
    </PostCardAuthProvider>
  );
}
